import { ConfirmAction, Datagrid, TooltipButton } from 'components';

import { ToolLike } from 'app/types/tool';
import { Card, Col, Row, Space, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, ToolOutlined } from '@ant-design/icons';
import { globalActions } from 'app/services/global';
import { Drawers } from 'app/types/drawer';
import { useDispatch } from 'react-redux';
import Actions from 'components/Actions/Actions';
import saveAs from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { User } from 'constants/user';
import { useDeleteToolMutation } from 'app/services/tools';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'constants/index';
import StatusIndicator from './StatusIndicator';
import { getToolStatus } from 'utils/tool';

type DetailsProps = {
  tool: ToolLike;
  readonly?: boolean;
};

const Details = ({ tool, readonly }: DetailsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [ deleteTool, deleteMeta ] = useDeleteToolMutation();

  const hasEditPermission = useHasPermission(User.Permissions.EDIT_TOOL);
  const hasDeletePermission = useHasPermission(User.Permissions.DELETE_TOOL);
  const canEditTool = !readonly && hasEditPermission;
  const canDeleteTool = !readonly && hasDeletePermission;
  const { isMaintenanceOk, isTestOk } = getToolStatus(tool);

  const getToolName = (tool: ToolLike) => {
    return `${tool.Make} ${tool.Model}`;
  };

  const handleClick = (drawer: string) => {
    dispatch(
      globalActions.openDrawer({
        key: drawer,
        props: { tool },
      }),
    );
  };

  useEffect(() => {
    if (deleteMeta.isSuccess) navigate(Paths.TOOLS_ACTIVE);
  }, [ deleteMeta.isSuccess ]);

  const actions = [];

  if (canEditTool) {
    actions.push(
      <TooltipButton
        key="edit"
        title={t('common.edit')}
        buttonProps={{
          onClick: () => handleClick(Drawers.EDIT_TOOL),
          icon: <EditOutlined />,
          type: 'link',
        }}
      />,
    );
  }

  if (canDeleteTool && !tool.HasProjects) {
    actions.push(
      <ConfirmAction
        callback={() => deleteTool(tool.Id as number)}
        confirmMessage={t('common.deleteconfirm', {
          content: `"${getToolName(tool)}"`,
        })}
        successMessage={t('common.deletesuccess', {
          content: `"${getToolName(tool)}"`,
        })}
        tooltipButtonProps={{
          buttonProps: {
            icon: <DeleteOutlined />,
            type: 'link',
            danger: true,
          },
          title: t('common.delete'),
        }}
        meta={deleteMeta}
        key="delete"
      />,
    );
  }

  const renderActiveTag = () => {
    if (tool.IsActive) return <Tag color={'success'}>{t('common.active')}</Tag>;
    return <Tag color={'error'}>{t('common.inactive')}</Tag>;
  };

  const header = tool && (
    <Row justify="space-between">
      <Col>
        <Space>
          <ToolOutlined />
          <div className="title">{getToolName(tool)}</div>
          <StatusIndicator isMaintenanceOk={isMaintenanceOk} isTestOk={isTestOk} />
          {renderActiveTag()}
        </Space>
      </Col>
      {!readonly && (
        <Col>
          <Actions actions={actions} />
        </Col>
      )}
    </Row>
  );

  const handleDownload = () => {
    if (!tool.CertificateUrl) return;
    const fileName = tool.CertificateFileName
      ? tool.CertificateFileName.replaceAll('"', '')
      : 'certificate';
    saveAs(tool.CertificateUrl, fileName);
  };

  return (
    <Card title={header}>
      <Datagrid
        data={tool}
        groupedKeys={[
          {
            keys: [
              'Make',
              'Model',
              'SerialNumber',
              'QrCode',
              'Power',
              'Airflow',
              'Category',
              'FirstUsageTime',
              'CertificateUrl',
            ],
          },
        ]}
        translationsNamespace="entities.tools"
        dateOnlyKeys={[ 'FirstUsageTime' ]}
        links={[
          {
            key: 'CertificateUrl',
            message: t('entities.tools.downloadcertificate'),
            onClick: handleDownload,
          },
        ]}
      />
    </Card>
  );
};

export default Details;
